import { useCallback, useEffect, useState } from 'react';
import { Button, Form, FormItem, Input, Password, SubTitle, Title, Row, Space, Paragraph, List, ListItem, ListMetaItem } from '@/components/atoms';
import { Alert, Divider, Spin } from 'antd';
import api from '@/api';
import { Link } from 'react-router-dom';
import { API_AUTH_CHANGE_PASSWORD, API_AUTH_CHECK, API_AUTH_CODE, API_AUTH_CONFIRM, PATH_AUTH_LOGIN } from '@/constants/constant';
import { ErrorMessage } from '@/constants/message';
import HttpStatus from 'http-status-codes';

type ForgotPageStateType = {
  loading: boolean;
  step: number;
  email: string;
  codeType: number;
  code: string;
  error: string;
  counter: number;
  to: string;
};

enum FindPasswordStep {
  CHECK_ACCOUNT = 0,
  REQUEST_CODE = 1,
  CONFIRM_CODE = 2,
  CHANGE_PASSWORD = 3,
  COMPLETE = 4,
}

const FindPasswordPage = () => {
  const [accountForm] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [passForm] = Form.useForm();
  const [state, setState] = useState<ForgotPageStateType>({
    loading: false,
    step: FindPasswordStep.CHECK_ACCOUNT,
    email: '',
    codeType: 0,
    code: '',
    error: '',
    counter: 0,
    to: '',
  });

  const checkEmail = useCallback(
    (values) => {
      setState({ ...state, loading: true });
      api
        .post(API_AUTH_CHECK, 'pauth.PbCheckRequest', { value: values.email, type: 'email', match: true })
        .then(() => {
          setState({ ...state, error: ErrorMessage.auth.email.notFound, loading: false });
        })
        .catch((e) => {
          switch (e.response?.status) {
            case HttpStatus.CONFLICT:
              setState({ ...state, step: FindPasswordStep.REQUEST_CODE, loading: false, email: values.email });
              break;
            default:
              setState({ ...state, error: ErrorMessage.network.disconnected, loading: false });
              break;
          }
        });
    },
    [state],
  );

  const authRequest = useCallback(
    (type: number) => {
      setState({ ...state, loading: true });
      api
        .post(API_AUTH_CODE, 'pauth.PbCodeRequest', { email: state.email, type })
        .then(({ data }) => {
          setState({ ...state, step: FindPasswordStep.CONFIRM_CODE, loading: false, counter: 180, codeType: type, to: data.to });
        })
        .catch((e) => setState({ ...state, error: e.message, loading: false }));
    },
    [state],
  );

  const sendCode = useCallback(
    (values) => {
      setState({ ...state, loading: true });
      api
        .post(API_AUTH_CONFIRM, 'pauth.PbConfirmRequest', { email: state.email, type: state.codeType, code: values.code })
        .then(() => {
          setState({ ...state, step: FindPasswordStep.CHANGE_PASSWORD, loading: false, counter: 0, code: values.code });
        })
        .catch((e) => setState({ ...state, error: e.message, loading: false }));
    },
    [state],
  );

  const changePass = useCallback(
    (values) => {
      setState({ ...state, loading: true });
      api
        .post(API_AUTH_CHANGE_PASSWORD, 'pauth.PbChangePasswordRequest', {
          email: state.email,
          type: state.codeType,
          code: state.code,
          password: values.password,
        })
        .then(() => {
          setState({ ...state, step: FindPasswordStep.COMPLETE, loading: false });
        })
        .catch((e) => setState({ ...state, error: e.message, loading: false }));
    },
    [state],
  );

  const describeAt = (step: number) => {
    switch (step) {
      case FindPasswordStep.CHECK_ACCOUNT:
        return '비밀번호를 재설정할 계정을 입력하세요.';
      case FindPasswordStep.REQUEST_CODE:
        return '비밀번호를 재설정을 위해 본인확인을 진행합니다.\n 방법을 선택하세요.';
      case FindPasswordStep.CONFIRM_CODE:
        return '인증번호를 입력하세요.';
      case FindPasswordStep.CHANGE_PASSWORD:
        return '새로운 비밀번호를 입력하세요.';
      default:
        return '';
    }
  };

  const layoutAt = (step: number) => {
    switch (step) {
      case FindPasswordStep.CHECK_ACCOUNT:
        return (
          <Form form={accountForm} onFinish={checkEmail} layout="vertical">
            <FormItem
              name="email"
              validateFirst
              rules={[
                { required: true, message: ErrorMessage.auth.email.empty },
                { type: 'email', message: ErrorMessage.auth.email.type },
              ]}
            >
              <Input placeholder="이메일 주소" />
            </FormItem>
            <FormItem>
              <Button
                htmlType="submit"
                type="success"
                size="large"
                block
                loading={state.loading}
                disabled={state.loading}
                style={{ margin: '20px auto' }}
              >
                다음
              </Button>
            </FormItem>
          </Form>
        );
      case FindPasswordStep.REQUEST_CODE:
        return (
          <>
            <List style={{ marginBottom: '30px' }}>
              <ListItem onClick={() => authRequest(1)} className="process-list">
                <ListMetaItem title="전화번호로 인증" description="회원가입시에 입력한 전화번호로 인증합니다." />
              </ListItem>
              <ListItem onClick={() => authRequest(0)} className="process-list">
                <ListMetaItem title="이메일로 인증" description="회원가입시에 입력한 이메일로 인증합니다." />
              </ListItem>
            </List>
          </>
        );
      case FindPasswordStep.CONFIRM_CODE:
        return (
          <>
            {state.counter <= 0 && (
              <>
                <Row justify="center" style={{ padding: '20px' }}>
                  <Space>
                    <Alert message="인증코드 유효시간이 만료되었습니다." type="error" />
                  </Space>
                </Row>
                <Button
                  type="primary"
                  block
                  loading={state.loading}
                  disabled={state.loading}
                  style={{ margin: '20px auto' }}
                  onClick={() => {
                    setState({
                      ...state,
                      step: 1,
                      email: '',
                      codeType: 0,
                      error: '',
                      counter: 0,
                      to: '',
                    });
                  }}
                >
                  재시도
                </Button>
              </>
            )}
            {state.counter > 0 && (
              <>
                <Row justify="center" style={{ padding: '20px' }}>
                  <Space>
                    {state.codeType === 0 && (
                      <Paragraph>
                        <strong>{state.to}</strong> 주소로 발송되었습니다.
                      </Paragraph>
                    )}
                    {state.codeType === 1 && (
                      <Paragraph>
                        <strong>{state.to}</strong>으로 끝나는 번호로 발송되었습니다.
                      </Paragraph>
                    )}
                  </Space>
                </Row>
                <Form form={codeForm} layout="vertical" onFinish={sendCode}>
                  <FormItem
                    name="code"
                    validateFirst
                    rules={[
                      { required: true, message: '인증코드를 입력하세요.' },
                      { pattern: /^[0-9]{6}$/, message: '6자리 숫자만 입력할 수 있습니다.' },
                    ]}
                  >
                    <Input placeholder="인증코드" addonAfter={`${state.counter}초`} />
                  </FormItem>

                  <FormItem>
                    <Button
                      type="dark"
                      htmlType="submit"
                      block
                      loading={state.loading}
                      disabled={state.loading || state.counter <= 0}
                      style={{ margin: '20px auto' }}
                    >
                      다음
                    </Button>
                  </FormItem>
                </Form>
              </>
            )}
          </>
        );
      case FindPasswordStep.CHANGE_PASSWORD:
        return (
          <Form form={passForm} onFinish={changePass} layout="vertical">
            <FormItem
              name="password"
              validateFirst
              rules={[
                { required: true, message: '세 비밀번호를 입력하세요.' },
                { min: 6, message: '6자 이상 입력하세요.' },
              ]}
            >
              <Password placeholder="새 비밀번호(6자 이상)" />
            </FormItem>
            <FormItem
              name="confirm"
              validateFirst
              dependencies={['password']}
              rules={[
                { required: true, message: '세 비밀번호를 다시 한번 입력하세요.' },
                { min: 6, message: '6자 이상 입력하세요.' },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    // eslint-disable-next-line prefer-promise-reject-errors
                    return Promise.reject('비밀번호가 일치하지 않습니다.');
                  },
                }),
              ]}
            >
              <Password placeholder="새 비밀번호 재입력" />
            </FormItem>
            <FormItem>
              <Button type="primary" htmlType="submit" block loading={state.loading} disabled={state.loading} style={{ margin: '20px auto' }}>
                저장
              </Button>
            </FormItem>
          </Form>
        );
      case FindPasswordStep.COMPLETE:
        return (
          <Row justify="center" align="middle" style={{ padding: '20px', flexDirection: 'column' }}>
            <Paragraph>비밀번호가 변경되었습니다.</Paragraph>
            <Button type="primary" block style={{ marginTop: '30px' }}>
              <Link to={PATH_AUTH_LOGIN}>로그인 하러가기</Link>
            </Button>
          </Row>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (state.counter > 0) {
      timer = setTimeout(() => setState({ ...state, counter: state.counter - 1, loading: false }), 1000);
    }
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <Spin spinning={state.loading}>
      <Row justify="center" align="middle" style={{ padding: '20px', flexDirection: 'column' }}>
        <Title>비밀번호 재설정</Title>
        <SubTitle>{describeAt(state.step)}</SubTitle>
      </Row>
      {layoutAt(state.step)}
      {![FindPasswordStep.COMPLETE].includes(state.step) && (
        <>
          <Row justify="center">
            <Link className="text-black link success-link" to={PATH_AUTH_LOGIN}>
              로그인 하러가기
            </Link>
          </Row>
        </>
      )}
    </Spin>
  );
};

export default FindPasswordPage;
